import { TPrimaryGoal } from "./types";

const MARKETING_HANDOFF_STORAGE_KEY = "marketing_handoff_signup_context_v1";
const MARKETING_HANDOFF_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type TMarketingHandoffStorage = {
  intent: string | null;
  primaryGoal: TPrimaryGoal | null;
  websiteUrl: string;
  capturedAt: number;
};

const LEAD_CAPTURE_INTENTS = new Set([
  "lead_capture",
  "lead_widget",
  "leadwidget",
  "lead-gen",
  "leadgen",
  "deploy_widget",
  "widget_install",
  "lead_acquisition",
]);

const normalizeWebsiteUrl = (value: string | null): string => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return "";

  const withProtocol = /^https?:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;

  try {
    const parsedUrl = new URL(withProtocol);
    const isHttpProtocol =
      parsedUrl.protocol === "https:" || parsedUrl.protocol === "http:";
    const hasLikelyHost =
      parsedUrl.hostname.includes(".") || parsedUrl.hostname === "localhost";

    if (!isHttpProtocol || !hasLikelyHost) return "";
    return parsedUrl.toString();
  } catch {
    return "";
  }
};

const mapIntentToPrimaryGoal = (intent: string | null): TPrimaryGoal | null => {
  if (!intent) return null;
  const normalizedIntent = intent.trim().toLowerCase();
  if (!normalizedIntent) return null;
  if (LEAD_CAPTURE_INTENTS.has(normalizedIntent)) return "lead_capture";
  return null;
};

const mapGoalToPrimaryGoal = (goal: string | null): TPrimaryGoal | null => {
  if (!goal) return null;
  if (
    goal === "lead_capture" ||
    goal === "in_office_simulations" ||
    goal === "clinical_documentation" ||
    goal === "before_after_marketing" ||
    goal === "other"
  ) {
    return goal;
  }
  return null;
};

const getMarketingHandoffFromSearchParams = (
  searchParams: URLSearchParams,
): TMarketingHandoffStorage | null => {
  const intent = searchParams.get("intent");
  const primaryGoalFromGoalParams =
    mapGoalToPrimaryGoal(searchParams.get("primary_goal")) ||
    mapGoalToPrimaryGoal(searchParams.get("goal"));
  const primaryGoal =
    primaryGoalFromGoalParams || mapIntentToPrimaryGoal(intent);

  const websiteUrl = normalizeWebsiteUrl(
    searchParams.get("website") || searchParams.get("website_url"),
  );

  if (!primaryGoal && !websiteUrl) return null;

  return {
    intent,
    primaryGoal,
    websiteUrl,
    capturedAt: Date.now(),
  };
};

const readStoredMarketingHandoff = (): TMarketingHandoffStorage | null => {
  if (typeof window === "undefined") return null;

  const rawValue = window.localStorage.getItem(MARKETING_HANDOFF_STORAGE_KEY);
  if (!rawValue) return null;

  try {
    const parsedValue = JSON.parse(rawValue) as TMarketingHandoffStorage;
    if (
      !parsedValue ||
      typeof parsedValue.capturedAt !== "number" ||
      Date.now() - parsedValue.capturedAt > MARKETING_HANDOFF_MAX_AGE_MS
    ) {
      window.localStorage.removeItem(MARKETING_HANDOFF_STORAGE_KEY);
      return null;
    }

    return {
      ...parsedValue,
      websiteUrl: normalizeWebsiteUrl(parsedValue.websiteUrl),
    };
  } catch {
    window.localStorage.removeItem(MARKETING_HANDOFF_STORAGE_KEY);
    return null;
  }
};

export const captureMarketingHandoffFromSearch = (
  searchParams: URLSearchParams,
): TMarketingHandoffStorage | null => {
  if (typeof window === "undefined") return null;

  const handoff = getMarketingHandoffFromSearchParams(searchParams);
  if (!handoff) return null;

  window.localStorage.setItem(
    MARKETING_HANDOFF_STORAGE_KEY,
    JSON.stringify(handoff),
  );
  return handoff;
};

export const getMarketingHandoffContext = (
  searchParams?: URLSearchParams,
): TMarketingHandoffStorage | null => {
  const fromSearch = searchParams
    ? getMarketingHandoffFromSearchParams(searchParams)
    : null;
  if (fromSearch) return fromSearch;
  return readStoredMarketingHandoff();
};

export const clearMarketingHandoffContext = (): void => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(MARKETING_HANDOFF_STORAGE_KEY);
};
